import React, { useMemo } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { useTranslation } from 'react-i18next';

import useStrapiPageData from './data/useStrapiPageData';
import useLocales from './data/useLocales';

import { isLocaleSweden } from '../utils/locale';

import { IconBWFooter } from '../../static/icons/iconFooter';
import { IconLinkedIn } from '../../static/icons/iconLinkedin';
import { IconFB } from '../../static/icons/logoFb';

import {
  PAGE_INDEX,
  PAGE_ABOUT_US,
  PAGE_BLOG,
  PAGE_FOR_VENDOR,
  PAGE_SYSTEMGUIDE,
  PAGE_CATEGORIES,
  PAGE_SITEMAP_HTML,
  // PAGE_TOPLISTS,
  PAGE_LEGAL_PRIVACY,
  PAGE_LEGAL_TERMS,
  PAGE_LEGAL_COOKIE,
  PAGE_INDUSTRY,
} from '../constants/strapi';
import useFetchSites from './data/useFetchSites';
import useTransformGatsbyData from './useTransformGatsbyData';

const NAV_QUERY = graphql`
  {
    bwNavigationData {
      data
    }

    allBwIndustry {
      totalCount
    }
  }
`;

const useNavigation = (siteUrls, location) => {
  const { t } = useTranslation();
  const { sites: bwSites } = useFetchSites();

  const [indexPageUrl] = useStrapiPageData(PAGE_INDEX);
  const [systemGuidePageUrl] = useStrapiPageData(PAGE_SYSTEMGUIDE);
  const [categoriesPageUrl] = useStrapiPageData(PAGE_CATEGORIES);
  const [industriesPageUrl] = useStrapiPageData(PAGE_INDUSTRY);
  const [blogPageUrl] = useStrapiPageData(PAGE_BLOG);
  const [aboutUsPageUrl] = useStrapiPageData(PAGE_ABOUT_US);
  const [forVendorsPageUrl] = useStrapiPageData(PAGE_FOR_VENDOR);
  // const [toplistsPageUrl] = useStrapiPageData(PAGE_TOPLISTS);
  const [sitemapPageUrl] = useStrapiPageData(PAGE_SITEMAP_HTML);
  const [systemguidePageUrl] = useStrapiPageData(PAGE_SYSTEMGUIDE);
  const [legalPrivacyPageUrl] = useStrapiPageData(PAGE_LEGAL_PRIVACY);
  const [legalTermsPageUrl] = useStrapiPageData(PAGE_LEGAL_TERMS);
  const [legalCookiesPageUrl] = useStrapiPageData(PAGE_LEGAL_COOKIE);

  const { locale } = useLocales();
  const isSweden = isLocaleSweden(locale);
  const siteUrlsObj = siteUrls[location?.pathname];
  const data = useStaticQuery(NAV_QUERY);
  const bwNavigationData = useTransformGatsbyData(data.bwNavigationData);
  const hasIndustries = data.allBwIndustry?.totalCount > 0;
  const sites = [];

  let locationOrigin = '';
  if (typeof window !== 'undefined') {
    locationOrigin = location.origin;
  } else {
    locationOrigin = process.env.GATSBY_SITE_URL;
    // locationOrigin = `https://businesswith.${locale.split('_')[1]?.toLowerCase()}`;
  }

  bwSites?.forEach((bwSite) => {
    const url = siteUrlsObj?.[bwSite.defaultLocale];
    const iso = bwSite.defaultLocale.split('_')[1]?.toLowerCase();
    sites.push({
      url: `https://${bwSite.domain}${url || ''}`,
      imgUrl: `${locationOrigin}/flags/${iso}.svg`,
      locale: bwSite.defaultLocale,
    });
  });

  const footers = useMemo(() => {
    const f = [
      {
        id: 'allCategories',
        text: t('common.allCategories'),
        href: categoriesPageUrl,
      },
    ];

    if (hasIndustries) {
      f.push({
        id: 'allIndustries',
        text: t('common.allIndustries'),
        href: industriesPageUrl,
      });
    }

    return f;
  }, [hasIndustries, categoriesPageUrl, industriesPageUrl, t]);

  const menuItems = useMemo(
    () =>
      [
        {
          id: 'systemguide',
          title: t('common.systemGuide'),
          href: systemGuidePageUrl,
        },
        {
          id: 'categories',
          title: t('common.systemCategories'),
          href: categoriesPageUrl,
          children: {
            footers,
            menuItems: bwNavigationData.topCategoriesMenuItems,
          },
        },
        // { id: 'blog', title: t('common.blog'), href: blogPageUrl },
        {
          id: 'forVendors',
          title: t('common.forVendors'),
          href: forVendorsPageUrl,
        },
        { id: 'aboutUs', title: t('common.aboutUs'), href: aboutUsPageUrl },
      ].filter((item) => !!item?.href),
    [bwNavigationData.topCategoriesMenuItems, t]
  );

  const footer = useMemo(
    () => ({
      bwName: isSweden ? 'BusinessWith Sweden AB' : 'BusinessWith',
      bwMail: 'info@businesswith.com',
      addresses: isSweden
        ? [
            {
              street: 'Wallingatan 29',
              zip: '111 24',
              city: 'Stockholm',
            },
            {
              street: 'Östra Hamngatan 16',
              zip: '411 09',
              city: 'Göteborg',
            },
          ]
        : [],
      footerItems: [
        {
          sectionType: 'single',
          component: {
            type: 'logoSection',
            componentType: 'logoSection',
            componentData: {
              logoImg: <IconBWFooter />,
              text: '', // Är tanken att vi kanske ska visa adressen här i Sverige?
              // TODO: Reviews by verified usersReviews by verified users Reviews by verified users Reviews by verified users',
              // buttonNewsLetter: 'TODO: Sign Up For Newsletter',
              iconsSocial: [
                {
                  name: 'facebook-icon',
                  iconImg: <IconFB />,
                  iconUrl:
                    'https://www.facebook.com/BusinessWith-softwares-2147009188950621/',
                },
                {
                  name: 'linkedin-icon',
                  iconImg: <IconLinkedIn />,
                  iconUrl:
                    'https://www.linkedin.com/company/businesswith-software/',
                },
                // {
                //   name: 'instagram-icon',
                //   iconImg: IconInsta,
                //   iconUrl: 'https://www.instagram.com/businesswith.se/',
                // },
              ],
            },
          },
        },
        {
          sectionType: 'single',
          component: {
            type: 'menuLinks',
            title: t('common.aboutUs'),
            componentData: [
              {
                name: t('common.aboutUs'),
                href: aboutUsPageUrl,
              },
              {
                name: t('common.career'),
                href: 'https://career.businesswith.se/',
              },
              {
                name: 'Privacy Policy',
                href: legalPrivacyPageUrl,
              },
              {
                name: 'Terms of use',
                href: legalTermsPageUrl,
              },
              {
                name: 'Cookie Policy',
                href: legalCookiesPageUrl,
              },
            ].filter((item) => !!item?.href),
          },
        },
        {
          sectionType: 'single',
          component: {
            type: 'menuLinks1',
            title: t('common.forVendors'),
            componentData: [
              {
                name: t('common.forVendors'),
                href: forVendorsPageUrl,
              },
              {
                name: t('common.portal'),
                href: 'https://app.businesswith.com/',
              },
            ].filter((item) => !!item?.href),
          },
        },
        {
          sectionType: 'single',
          component: {
            type: 'menuLinks2',
            title: t('common.quickLinks'),
            componentData: [
              { id: 'blog', name: t('common.blog'), href: blogPageUrl },
              {
                name: t('common.systemGuide'),
                href: systemguidePageUrl,
              },
              {
                name: t('common.allCategories'),
                href: categoriesPageUrl,
              },
              {
                name: t('common.sitemap'),
                href: sitemapPageUrl,
              },
            ].filter((item) => !!item?.href),
          },
        },
        {
          sectionType: 'single',
          component: {
            type: 'sites',
            componentData: sites,
          },
        },
      ],
    }),
    [sites]
  );

  return {
    home: indexPageUrl,
    menu: {
      menuItems,
    },
    footer,
  };
};

export default useNavigation;
